import * as React from "react";
import { MaterialTable } from "../MaterialTable/MaterialTable";
import Link from "@material-ui/core/Link/Link";
import Typography from "@material-ui/core/Typography/Typography";
import {
    IUniversalTableAction,
    IUniversalTableAddAction,
    IUniversalTableColumn,
    IUniversalTableColumnActions,
    IUniversalTableData,
    IUniversalTableDeleteAction,
    IUniversalTableDetailPanelColumn,
    IUniversalTableEditAction,
    IUniversalTableOwnAction,
    IUniversalTableTableAction,
    IUniversalTableTwoStatesAction
} from "./interfaces";
import { DetailPanel } from "./DetailPanel";
import { useTranslation } from "react-i18next";
import TextField from "@material-ui/core/TextField/TextField";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./UniversalTable.styles";
import { InputAdornment, Paper, Tooltip, WithStyles } from "@material-ui/core";
import {
    convertDateStringToNumber,
    convertDateToFullDateTime,
    convertNumberToDateString,
    convertNumberToDateStringWithFormat,
    mapNumberToDate,
    today
} from "../../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../../constants/TIME";
import { isPermissionGranted } from "../../../utilities/permissionUtils";
import { useGetRoles } from "../../../hooks/useGetRole";
import { bytesToHumanReadable, searchCaseInsensitive, stringComparator } from "../../../utilities/generalUtils";
import { formatPrice } from "../../../utilities/PriceUtils";
import AddBoxIcon from "@material-ui/icons/AddBox";
import { FormNativeSelect } from "../FormComponents/FormNativeSelect";
import { FormDateField } from "../FormComponents/FormDateField";
import { BlueButton } from "../Buttons/BlueButton";
import { DateWithOptionEdit } from "../DateWithOptionEdit/DateWithOptionEdit";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import { OrderDetailsAccordion } from "./OrderDetailsAccordion";
import { findProductByName } from "../../views/productOverview/ProductHelper";
import { allVisibilityCategories, IVisibilityType, VisibilitySelect } from "../VisibilitySelect/VisibilitySelect";

export interface IUniversalTableProps extends WithStyles<typeof styles> {
    title?: string; // table's title
    columns: IUniversalTableColumn[]; // definition of columns
    data: IUniversalTableData; // data
    pagingDisabled?: boolean; // if the paging is disabled
    pageSize?: number; // page size, if not present, 10 is default
    searchable?: boolean; // if used, the search bar is shown
    detailPanelTableColumns?: IUniversalTableDetailPanelColumn[]; // columns of the table shown in the detail panel
    dense?: boolean; // dense padding
    detailPanel?: any;
    isLoading?: boolean; // if true, it shows the loading circle
    // selectable?: boolean; // TODO: will be added
    // onSelectChange?: () => {}; // TODO: will be added
    emptyDataMessage?: string;
    onRowClick?: any;
    selectedColumnFunction?: any;
    tableRef?: any; // to bind ref of the table
    export?: boolean;
    exportFilename?: string;
}

const UniversalTable: React.FunctionComponent<IUniversalTableProps> = props => {
    const isNotSortable = type => {
        return type === "actions" || type === "button";
    };

    const isCustomRender = type => {
        return type === "link"
            || type === "button"
            || type === "price"
            || type === "size"
            || type === "date"
            || type === "dateTime"
            || type === "date_with_option"
            || type === "component"
            || type === "countries"
            || type === "select"
            || type === "multiselect"
            || type === "products"
            || type === "visibility_select";
    };

    const roles = useGetRoles();

    const checkPermissions = (columns: IUniversalTableAction[], roles: string[]): IUniversalTableAction[] => {
        if (!columns) return [];
        return columns?.filter(column => !column.scope || isPermissionGranted(roles, column.scope));
    };

    const {classes} = props;

    const actionColumn = props.columns.find(column => column.type === "actions") as IUniversalTableColumnActions;
    const actions = checkPermissions(actionColumn?.actions, roles);
    const editAction = actions.find(action => action.name === "edit") as IUniversalTableEditAction;
    const addAction = actions.find(action => action.name === "add") as IUniversalTableAddAction;
    const deleteAction = actions.find(action => action.name === "delete") as IUniversalTableDeleteAction;

    const isAnyTableAction =
        addAction !== undefined || actions?.some(action => action["tableAction"] !== undefined);

    const tableRef = props.tableRef || React.createRef();
    const {t} = useTranslation();

    const detailPanel = props.detailPanelTableColumns
        ? [
            {
                tooltip: t("universalTable.details"),
                render: rowData => {
                    const dataToRender = {...rowData};
                    delete dataToRender.tableData; // remove the material-table data
                    return <DetailPanel columns={props.detailPanelTableColumns} data={[dataToRender]} />;
                }
            }
        ]
        : null;

    const pageSize = props.pageSize || 10;

    const getValueFromUnion = (union: string | ((index: number) => string), index: number): string => {
        return (union instanceof Function) ? union(index) : union;
    }

    return (
        <div className={classes.table}>
            <MaterialTable
                tableRef={tableRef}
                title={<Typography variant="h5">{props.title} </Typography>}
                isLoading={props.isLoading}
                components={{
                    Container: props => <Paper {...props} elevation={0} />
                }}
                columns={props.columns
                    .filter(column => column.type !== "actions")
                    .map(column => ({
                        title: t(column.title),
                        ...((column.type === "text" || column.type === "number" || column.type === "multiline_text" || column.type === "date" || column.type === "price" || column.type === "link" || 
                            column.type === "date_with_option" || column.type === "boolean" || column.type === "countries" || column.type === "select" || column.type === "multiselect" || column.type === "products" || column.type === "visibility_select") && {
                            field: column.field
                        }),
                        ...(column.type === "boolean") && {
                            lookup: {
                                false: column.falseText || "No",
                                true: column.trueText || "Yes",
                            }
                        },
                        sorting: !isNotSortable(column.type) && !column.sortDisable,
                        editable: column.editable || "always",
                        align: column.align || "left",
                        defaultSort: column.defaultSort,
                        ...(isCustomRender(column.type) && {
                            render: (rowData: any) => {
                                switch (column.type) {
                                    case "link":
                                        const field = rowData[column.field];
                                        const hrefUsed = column.linkField;
                                        const linkParams = hrefUsed
                                            ? {
                                                href: rowData[column.linkField]
                                            }
                                            : {
                                                onClick: () => column.onClick(rowData.tableData.id)
                                            };
                                        const target = column.openInNewTab ? {target: "_blank"} : {};
                                        const isLinkAvailable = rowData[column.linkField] !== null;
                                        if (!isLinkAvailable)
                                            return column.titleIfLinkNotAvailable || rowData[column.fieldIfLinkNotAvailable] || "";
                                        else return (
                                            <Link className={classes.link} {...linkParams} {...target}>
                                                {column.linkTitle || field }
                                            </Link>
                                        )
                                    case "button":
                                        const isDisabled = column.disabled(rowData.tableData.id);
                                        const disabledTooltip = getValueFromUnion(column?.disabledTooltip, rowData.tableData.id);
                                        return (
                                            <Tooltip title={isDisabled ? disabledTooltip : ""}>
                                                <span>
                                                    <BlueButton
                                                        style={{margin: 0}}
                                                        onClick={() => column.onClick(rowData.tableData.id)}
                                                        disabled={isDisabled}
                                                    >
                                                        {column.buttonTitle}
                                                    </BlueButton>
                                                </span>
                                            </Tooltip>
                                        );
                                    case "actions":
                                        return null;
                                    case "date": {
                                        return convertNumberToDateStringWithFormat(rowData[column.field], column.format);
                                    }
                                    case "date_with_option":
                                        let value = "";
                                        if (rowData[column.field] === 0)
                                            value = column.label;
                                        else if (rowData[column.field] === undefined)
                                            value = "";
                                        else
                                            value = convertNumberToDateString(rowData[column.field]);
                                        return value;
                                    case "dateTime":
                                        return convertDateToFullDateTime(rowData[column.field]);
                                    case "price":
                                        return formatPrice(rowData[column.field]);
                                    case "size":
                                        return bytesToHumanReadable(rowData[column.field]);
                                    case "component":
                                        return column.component(column.field ? rowData[column.field] : rowData, rowData.tableData.id);
                                    case "products":
                                        const allProducts = column.allProducts;                                    
                                        function getNameForSimpleOrder(items) {
                                            if (items.length > 0) {
                                                return findProductByName(allProducts, items[0].product)?.name;
                                            }
                                            return t("general.customData");
                                        }
                                        const items = rowData[column.field];
                                        return items.length > 1
                                            ? <OrderDetailsAccordion rowId={rowData.tableData.id} items={items}/>
                                            : <Typography>{getNameForSimpleOrder(items)}</Typography>;
                                    case "countries":
                                        const values = rowData[column.field];
                                        return Array.isArray(values) ? values?.join(", ") : values;
                                    case "select":
                                        return rowData[column.field]?.label;
                                    case "multiselect":
                                        return rowData[column.field]?.map(i => i.label)?.join(", ");
                                    case "visibility_select":
                                        return convertNumberToDateString(rowData[column.field]) || "Unrestricted";
                                    default:
                                        return rowData[column.field];
                                }
                            }
                        }),
                        ...(column.type === "component" && {
                            width: column.width + "",
                            ...(column.searchFunc && {
                                customFilterAndSearch: (term, rowData) => column.searchFunc(term, column.field ? rowData[column.field] : rowData)
                            }),
                            ...(column.customSort && {
                                    customSort: (a, b) => column.customSort?.(a, b)
                            })
                        }),
                        ...(column.type === "price" && {
                            customSort: (a, b) => a[column.field] - b[column.field],
                            editComponent: (props: any) => (
                                <TextField
                                    type="number"
                                    value={props.value}
                                    helperText={props?.helperText || " "}
                                    variant="outlined"
                                    onChange={(event: any) => props.onChange(event.target.value)}
                                    error={props.error}
                                    className={classes.priceField}
                                    InputProps={{
                                        startAdornment: <InputAdornment style={{padding: 0}} position="start">€</InputAdornment>,
                                        inputProps: {
                                            min: column.min || 0
                                        }
                                    }}
                                />
                            ),
                            initialEditValue: column.defaultValue || 0,
                        }),
                        ...(column.type === "boolean" && {
                            customSort: (a, b) => {
                                const textA = a[column.field] ? (column.trueText || "Yes") : (column.falseText || "No");
                                const textB = b[column.field] ? (column.trueText || "Yes") : (column.falseText || "No");
                                return stringComparator(textA, textB);
                            }
                        }),
                        ...(column.type === "size" && {
                            customSort: (a, b) => a[column.field] - b[column.field]
                        }),
                        ...(column.type === "dateTime" && {
                            customSort: (a, b) => a[column.field] > b[column.field] ? 1 : -1
                        }),
                        ...(column.type === "text" && {
                            customSort: (a, b) => {
                                if (typeof a[column.field] === "string")
                                    return stringComparator(a[column.field], b[column.field])
                                else
                                    return a[column.field] > b[column.field] ? 1 : -1;
                            },
                            editComponent: (props: any) => {
                                return (
                                    <TextField
                                        className={classes.inputField}
                                        variant="outlined"
                                        value={props.value || ""}
                                        onChange={(event: any) => props.onChange(event.target.value)}
                                        error={props.error}
                                        helperText={props?.helperText || " "}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                );
                            }
                        }),
                        ...(column.type === "number" && {
                            customSort: (a, b) => a[column.field] - b[column.field],
                            editComponent: (props: any) => (
                                <TextField
                                    type="number"
                                    value={props.value}
                                    helperText={props?.helperText || " "}
                                    variant="outlined"
                                    onChange={(event: any) => props.onChange(event.target.value)}
                                    error={props.error}
                                    InputProps={{
                                        inputProps: {
                                            min: column.min || 0
                                        }
                                    }}
                                />
                            ),
                        }),
                        ...(column.type === "multiline_text" && {
                            editComponent: (props: any) => {
                                return (
                                    <TextField
                                        className={classes.inputField}
                                        variant="outlined"
                                        multiline
                                        rows={column.noLines || 5}
                                        value={props.value || ""}
                                        onChange={(event: any) => props.onChange(event.target.value)}
                                        error={props.error}
                                        helperText={props?.helperText || " "}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                );
                            }
                        }),
                        ...(column.type === "products" && {
                            customFilterAndSearch: (term, rowData: any) => {
                                return rowData.items.length > 0
                                    ? rowData.items?.some(orderItem => {
                                          const productName = findProductByName(
                                              column.allProducts,
                                              orderItem?.product
                                          )?.name;
                                          return searchCaseInsensitive(term, productName);
                                      })
                                    : searchCaseInsensitive(term, t("general.customData"));
                            },
                            width: "500"
                        }),
                        ...(column.type === "link" &&
                            column.linkTitle === undefined && {
                                customFilterAndSearch: (term, rowData) => searchCaseInsensitive(term, rowData[column.field].toString()),
                                customSort: (a, b) => {
                                    if (typeof a[column.field] === "string")
                                        return stringComparator(a[column.field].toString(), b[column.field].toString());
                                    else
                                        return a[column.field] > b[column.field] ? 1 : -1;
                                }
                            }),
                        ...(column.type === "text" &&
                            editAction &&
                            editAction.editFields?.find(editField => editField.field === column.field) && {
                                editComponent: (props: any) => {
                                    const field = editAction.editFields?.find(
                                        editField => editField.field === column.field
                                    );
                                    let options = field?.options || [];
                                    if (
                                        field?.getAdditionalRowOptions !== undefined &&
                                        props.rowData?.tableData?.id >= 0
                                    ) {
                                        const additionalRowOptions =
                                            field?.getAdditionalRowOptions(props.rowData?.tableData?.id) || [];
                                        options = [...new Set([...additionalRowOptions, ...options])]; // unique values
                                    }
                                    return (
                                        <FormNativeSelect
                                            className={classes.select}
                                            label=""
                                            options={options.map(option => ({name: option, shortcut: option}))}
                                            value={props.value || options[0]}
                                            onChange={(event: any) => props.onChange(event.target.value)}
                                        />
                                    );
                                },
                                ...(editAction.editFields?.find(editField => editField.field === column.field)
                                    ?.options?.[0] && {
                                    initialEditValue: editAction.editFields?.find(
                                        editField => editField.field === column.field
                                    )?.options?.[0]
                                })
                            }),
                        ...(column.type === "date" && {
                            editComponent: (props: any) => (
                                <FormDateField
                                    error={props.error}
                                    helperText={props.helperText || " "}
                                    placeholder=""
                                    value={mapNumberToDate(props.value).format(DATE_FORMAT) || today().format(DATE_FORMAT)}
                                    onChange={(value: any) => props.onChange(convertDateStringToNumber(value))}
                                    disabled={false}
                                    allowFuture
                                    firstAvailableDate={mapNumberToDate(column.firstAvailableDate).format(DATE_FORMAT) || null}
                                    lastAvailableDate={mapNumberToDate(column.lastAvailableDate).format(DATE_FORMAT) || null}
                                />
                            ),
                            initialEditValue: column.defaultValue || today().format(DATE_FORMAT),
                            validate: rowData => rowData[column.field] !== 0
                        }),
                        ...(column.type === "date_with_option" && {
                            editComponent: (props: any) => {
                                return <DateWithOptionEdit
                                    error={props.error}
                                    helperText={props.helperText}
                                    label={column.label}
                                    value={props.value}
                                    firstAvailableDate={column.firstAvailableDate}
                                    onChange={(value) => {
                                        props.onChange(value);
                                    }}
                                />;
                            },
                            initialEditValue: column.defaultValue !== undefined ? column.defaultValue : convertDateStringToNumber(today().format(DATE_FORMAT)),
                        }),
                        ...(column.type === "countries" && {
                            editComponent: (props: any) => {
                                return (
                                <Autocomplete
                                        id="userSelect"
                                        options={column.allCountries}
                                        getOptionLabel={(option: any) => option}
                                        multiple={column.multiple || null}
                                        value={props.value}
                                        renderInput={params => <TextField {...params} variant="outlined" error={props.error} helperText={props.helperText || " "}/>}
                                        onChange={(event, selectedCountries) => props.onChange(selectedCountries)}
                                    />)
                            },
                        }),
                        ...(column.type === "select" && {
                            editComponent: (props: any) => {
                                return (
                                <Autocomplete
                                        id={`select_${column.field}`}
                                        options={column.allValues}
                                        getOptionLabel={(option: any) => option.label}
                                        value={props.value}
                                        getOptionSelected={(option, value) => option.id === value.id}
                                        renderInput={params => <TextField {...params} variant="outlined" error={props.error} helperText={props.helperText}/>}
                                        onChange={(event, selectedIds) => props.onChange(selectedIds)}
                                    />)
                            },
                            customFilterAndSearch: (term, rowData) => searchCaseInsensitive(term, rowData[column.field]?.label.toString()),
                            customSort: (a, b) => stringComparator(a[column.field]?.label,  b[column.field]?.label)
                        }),
                        ...(column.type === "multiselect" && {
                            editComponent: (props: any) => {
                                return (
                                <Autocomplete
                                        id="userSelect"
                                        options={column.allValues}
                                        getOptionLabel={(option: any) => option.label}
                                        multiple
                                        value={props.value}
                                        getOptionSelected={(option, value) => option.id === value.id}
                                        renderInput={params => <TextField {...params} variant="outlined" error={props.error} helperText={props.helperText}/>}
                                        onChange={(event, selectedIds) => props.onChange(selectedIds)}
                                    />)
                            },
                        }),
                        ...(column.type === "boolean" && {
                            initialEditValue: column.defaultValue || false,
                        }),
                        ...(column.type === "visibility_select" && {
                            editComponent: (props: any) => {
                                const availableCategories = allVisibilityCategories?.filter(c => column.categories?.includes(c.type));

                                const getVisibilityType = (date: number, visibilityDate: number): IVisibilityType => availableCategories.find(c => c.condition(date, visibilityDate))?.type;
                                const getDateFromSelection = (value: IVisibilityType, newDate?: number): number => availableCategories?.find(c => c.type === value)?.calculateVisibility(newDate || date);

                                const date = props.rowData[column.dateField];
                                const visibilityDate = props.rowData[column.field];
                                const visibilityType = getVisibilityType(date, visibilityDate);
                                // when a source column is Unrestricted, "start_date", "plus_2_years", "plus_7_days" make no sense - filter them out 
                                const categoriesFiltered = column.categories?.filter(c => !(date === 0 && ["start_date", "plus_2_years", "plus_7_days"].includes(c)))

                                return (
                                    <VisibilitySelect
                                        error={props.error}
                                        helperText={props.helperText}
                                        categories={categoriesFiltered}
                                        value={visibilityType}
                                        onChange={(value, newDate) => props.onChange(getDateFromSelection(value, newDate))}
                                        date={visibilityDate}
                                        onDateChange={newDate => props.onChange(newDate)}
                                    />
                                );
                            },
                            initialEditValue:
                                column.defaultValue !== undefined ? column.defaultValue : convertDateStringToNumber(today().format(DATE_FORMAT))
                        }),
                        ...(column.validate && {
                            validate: rowData => column.validate(rowData)
                        })
                    }))}
                data={props.data || []}
                actions={[
                    ...(actions
                        ?.filter(action => action.name !== "edit")
                        ?.filter(action => action.name !== "delete")
                        ?.filter(action => action.name !== "add")
                        ?.filter(action => action["tableAction"] === undefined)
                        ?.filter(action => action["twoStatesAction"] === undefined)
                        ?.map((action: IUniversalTableOwnAction) => rowData => {
                            const tooltip = rowData.actionsDisabled?.includes(action.name)
                                ? getValueFromUnion(action.disabledTooltip, rowData.tableData.id)
                                : action.tooltip;
                            return {
                                icon: () => action.icon,
                                tooltip,
                                onClick: (event, rowData: any) => {
                                    action.onClick(rowData.tableData.id);
                                },
                                disabled: rowData.actionsDisabled?.includes(action.name) || false
                            };
                        }) || []),
                    ...(actions
                        ?.filter(action => action["twoStatesAction"] !== undefined)
                        ?.map((action: IUniversalTableTwoStatesAction) => rowData => {
                            const tooltip = rowData.actionsDisabled?.includes(action.name)
                                ? getValueFromUnion(action.disabledTooltip, rowData.tableData.id)
                                : action.trueTooltip && action.falseTooltip
                                ? rowData[action.stateField]
                                    ? action.trueTooltip
                                    : action.falseTooltip
                                : action.tooltip;
                            return {
                                icon: () => (rowData[action.stateField] ? action.trueIcon : action.falseIcon),
                                tooltip,
                                onClick: () => {
                                    action.onClick(rowData.tableData.id);
                                },
                                disabled: rowData.actionsDisabled?.includes(action.name) || false
                            };
                        }) || []),
                    ...(actions
                        ?.filter(action => action["tableAction"] !== undefined)
                        ?.map((action: IUniversalTableTableAction) => ({
                            icon: () => action.icon,
                            isFreeAction: true,
                            tooltip: action.tooltip,
                            onClick: () => {
                                action.onClick();
                            }
                        })) || []),
                    ...(addAction?.disabled
                        ? [
                            {
                                icon: () => <AddBoxIcon />,
                                isFreeAction: true,
                                tooltip: getValueFromUnion(addAction.disabledTooltip, 0) || "Disabled",
                                onClick: () => {
                                    // mandatory callback
                                },
                                disabled: true
                            }
                        ]
                        : [])
                ]}
                options={{
                    ...(props.export && {
                        exportButton: {
                            csv: props.export || false
                        },
                        exportFileName: props.exportFilename || "table_export.csv",
                        exportAllData: true
                    }),
                    actionsColumnIndex: -1,
                    draggable: false,
                    // paging: props.paging && props.data?.length > props.pageSize,
                    paging: !props.pagingDisabled,
                    pageSize: pageSize,
                    pageSizeOptions: [pageSize] || [10],
                    paginationType: "normal",
                    emptyRowsWhenPaging: false,
                    search: props.searchable || false,
                    searchFieldAlignment: "right",
                    addRowPosition: "first",
                    headerStyle: {
                        backgroundColor: "#f0f0f0"
                    },
                    toolbar: props.searchable || props.title !== undefined || isAnyTableAction,
                    showTitle: props.title !== undefined,
                    padding: props.dense ? "dense" : "default",
                    rowStyle: rowData => ({
                        backgroundColor: props.selectedColumnFunction && props.selectedColumnFunction(rowData) ? "#009" : "#fff",
                        color: props.selectedColumnFunction && props.selectedColumnFunction(rowData) ? "white" : "#333"
                    })
                }}
                localization={{
                    header: {
                        ...(actionColumn && {
                            actions: actionColumn.title
                        })
                    },
                    body: {
                        addTooltip: addAction?.tooltip || t("universalTable.add"),
                        deleteTooltip: deleteAction?.tooltip || t("general.delete"),
                        editTooltip: editAction?.tooltip || t("general.edit"),
                        editRow: {
                            deleteText: deleteAction?.deleteQuestion || t("universalTable.deleteText"),
                            cancelTooltip: t("universalTable.cancel"),
                            saveTooltip: t("general.save")
                        },
                        emptyDataSourceMessage: props.emptyDataMessage || t("universalTable.emptyData")
                    },
                    pagination: {
                        firstTooltip: t("universalTable.firstPage"),
                        firstAriaLabel: t("universalTable.firstPage"),
                        previousTooltip: t("universalTable.previousPage"),
                        nextTooltip: t("universalTable.nextPage"),
                        lastTooltip: t("universalTable.lastPage")
                    },
                    toolbar: {
                        searchTooltip: t("universalTable.search"),
                        searchPlaceholder: t("universalTable.search")
                    }
                }}
                detailPanel={props.detailPanel ? props.detailPanel : detailPanel}
                editable={{
                    isDeletable: (rowData: any) => !rowData?.actionsDisabled?.includes("delete"),
                    isEditable: (rowData: any) => !rowData?.actionsDisabled?.includes("edit"),
                    editTooltip: (rowData: any) => {
                        const disabledTooltip = getValueFromUnion(editAction?.disabledTooltip, rowData.tableData.id);
                        return rowData?.actionsDisabled?.includes("edit") ? disabledTooltip : editAction?.tooltip;
                    },
                    deleteTooltip: (rowData: any) => {
                        const disabledTooltip = getValueFromUnion(deleteAction?.disabledTooltip, rowData.tableData.id);
                        return rowData?.actionsDisabled?.includes("delete") ? disabledTooltip : deleteAction?.tooltip;
                    },
                    ...(addAction &&
                        !addAction.disabled && {
                            onRowAdd: newData =>
                                new Promise((resolve, reject) => {
                                    addAction.onAddFinished(newData);
                                    resolve(0);
                                })
                        }),
                    ...(editAction && {
                        onRowUpdate: (newData, oldData: any) =>
                            new Promise((resolve, reject) => {
                                editAction.onEditFinished(oldData.tableData.id, newData);
                                resolve(0);
                            })
                    }),
                    ...(deleteAction && {
                        onRowDelete: (oldData: any) =>
                            new Promise((resolve, reject) => {
                                deleteAction.onDeleteFinished(oldData.tableData.id);
                                resolve(0);
                            })
                    })
                }}
                onRowClick={props.onRowClick}
            />
        </div>
    );
};

export default withStyles(styles)(UniversalTable);
